import { QuoteSearchRequestQuery, QuoteSearchResponse } from '@lio/api-types';
import { config } from '@services/config';
import { buildQuoteSearchUrl } from '@services/quotes/helpers';
import { QuoteSearchRequestQueryWithOptionalType } from '@services/quotes/helpers/buildQuoteSearchUrl';

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { buildQueryParamForArray } from '@utils/buildQueryParamForArray';

export const quoteSearchQueryKeys = {
  search: ['quotes'] as const,
  searchWithParams: (params: QuoteSearchRequestQueryWithOptionalType) =>
    [...quoteSearchQueryKeys.search, params] as const,
};

export const useQuoteSearch = (params: QuoteSearchRequestQueryWithOptionalType, enabled: boolean = true) => {
  const client = useClient();
  const quoteSearchUrl = buildQuoteSearchUrl(params);

  return useQuery<QuoteSearchResponse, Error>({
    queryKey: quoteSearchQueryKeys.searchWithParams(params),
    queryFn: () => client.get(`quote?${quoteSearchUrl?.queryParams}`).json<QuoteSearchResponse>(),
    enabled: enabled && quoteSearchUrl !== undefined,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    staleTime: 0, // we intentionally want this to requery when you navigate back to the page
  });
};

export const QuoteSearchKeys = {
  search: ['quoteSearch'] as const,
  searchParams: (params: QuoteSearchRequestQuery) => [...QuoteSearchKeys.search, params] as const,
};

const getQueryParams = (params: QuoteSearchRequestQuery) => {
  const { sort, pageLimit, pageOffset, customerId, statuses, quoteType } = params;
  const query = new URLSearchParams();

  if (sort) {
    query.append('sort', sort);
  }

  if (pageLimit) {
    query.append('pageLimit', String(pageLimit));
  }

  if (pageOffset) {
    query.append('pageOffset', String(pageOffset));
  }

  if (customerId) {
    query.append('customerId', customerId);
  }

  if (statuses) {
    statuses.forEach((status) => {
      query.append('statuses', status);
    });
  }

  if (quoteType) {
    buildQueryParamForArray(query, 'quoteType', quoteType);
  }

  // TODO/consider (useQuoteSearchForCustomer) is like a mini version of useQuoteSearch, but only for customers.
  // we should consider merging these two hooks if they share enough logic.

  return query.toString();
};

export const useQuoteSearchForCustomer = (params: QuoteSearchRequestQuery, enabled: boolean = true) => {
  const client = useClient();

  const query = getQueryParams({ ...params });

  return useQuery<QuoteSearchResponse, Error>({
    queryKey: QuoteSearchKeys.searchParams({ ...params }),
    queryFn: () => client.get(`quote?${query}`).json<QuoteSearchResponse>(),
    enabled: !!(enabled && params.customerId !== undefined),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    ...config,
  });
};
